<script setup lang="ts">
import BaseCard from "@/core/components/base/BaseCard.vue";
import HeaderBackButton from "@/core/components/header-back-button/HeaderBackButton.vue";
import {useCommunicationStore} from "@/communication/stores/communication.store";
import {computed, ref} from "vue";
import {MassCertificationCustomer} from "@/core/interfaces/MassCertification";
import CommunicationCustomerRow from "@/communication/components/customer-list/CustomerBaseInformation.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {massCertificationApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import {useRouter} from "vue-router";
import CommunicationCustomMessageTemplate from "@/communication/components/CommunicationCustomMessageTemplate.vue";
import Icon from "@/core/components/icon/Icon.vue";
import {storeToRefs} from "pinia";
import {useCreditStore} from "@/stores/credit.store";
import {pluralize} from "@/core/filters/Pluralize";
import CreditStoreModal from "@/credit/components/CreditStoreModal.vue";
import NotificationTemplate from "@/notification-template/components/NotificationTemplate.vue";

const {customers, template, content, contentCost, mode, selectionBatchId, from, programId} = useCommunicationStore();
const {showSuccess, showError} = useToastStore();
const router = useRouter();
const {credit} = storeToRefs(useCreditStore());
const creditStoreModalVisible = ref(false);

/**
 * Ids of the checked customers. Selecting by id keeps each row check constant-time:
 * comparing whole customer objects made a campaign of a few thousand patients freeze the app.
 */
const selectedIds = ref(new Set<number>(customers.map(c => c.id)));

/**
 * Checked customers, in the original order.
 */
const selectedCustomers = computed<MassCertificationCustomer[]>(() => {
    return customers.filter(c => selectedIds.value.has(c.id));
});

/**
 * Check or uncheck a customer.
 *
 * @param customerId  The customer id.
 * @param checked     Whether the customer is checked.
 */
const toggleCustomer = (customerId: number, checked: boolean | null) => {
    if (checked) {
        selectedIds.value.add(customerId);
    } else {
        selectedIds.value.delete(customerId);
    }
};

/**
 * Action when confirm button is pressed.
 */
const onConfirm = () => {
    if (mode === 'notification') {
        return sendNotification();
    } else {
        return sendSms();
    }
};

/**
 * Send sms to customers.
 */
const sendSms = () => {
    if (template || content) {
        const customerParam = selectionBatchId
            ? {batch_selection_id: selectionBatchId}
            : {customer_ids: selectedCustomers.value.map(c => c.id)};

        const params = content
            ? {
                content,
                ...customerParam,
                ...(programId !== null && { program_id: programId })
            }
            : {
                notification_template_id: template!.id,
                ...customerParam
            };

        massCertificationApi().massCertification(params)
            .then(() => showSuccess({content: "\u{1F3C1} SMS envoyés avec succès"}))
            .catch(() => showError({content: "Erreur lors de l’envoi des SMS"}))
            .finally(() => {
                router.push(from ?? {name: 'patient-administration.communication.customers'});
            });
    }
};

/**
 * Send notification to customers.
 */
const sendNotification = async () => {
    if (!template) {
        return Promise.resolve();
    }

    const params = {
        notification_template_id: template.id,
        customer_ids: selectedCustomers.value.map(c => c.id)
    };

    return massCertificationApi().notifications(params)
        .then(() => showSuccess({content: "\u{1F3C1} Notifications envoyées avec succès"}))
        .catch(() => showError({content: "Erreur lors de l’envoi des notifications"}))
        .finally(() => {
            router.push({name: 'patient-administration.communication.customers'});
        });
};

/**
 * Cost of the message.
 */
const cost = computed(() => {
    return selectedCustomers.value.length * (contentCost || template?.credits || 1);
});

/**
 * Check if organization has enough credit to send the message.
 */
const hasEnoughCredit = computed(() => {
    return (credit.value?.balance ?? 0) >= cost.value;
});

/**
 * Check if a message can be sent.
 */
const canSend = computed(() => {
    if (hasEnoughCredit.value) {
        return true;
    }

    return mode === 'notification';
});
</script>

<template>
  <div class="h-full flex flex-col relative">
    <HeaderBackButton :title="'Communication par '+ mode" />
    <BaseCard class="m-4 p-4 flex flex-col">
      <h2>Confirmez l'envoi</h2>
      <p v-if="mode==='sms'" class="py-1 text-sm italic">
        <Icon name="mdi-alert text-orange" :size="12" />
        L’envoi de SMS est autorisé du lundi au vendredi de 08h00 à 20h00, et le samedi de 10h00 à 19h00, hors jours
        fériés.
      </p>
      <NotificationTemplate
        v-if="template && !content" :class="{
          'self-center': template.type === 'rcs'
        }" :template="template" :selectable="false"
      />
      <CommunicationCustomMessageTemplate v-if="content" :content="content" />
      <p v-if="customers.length > 0" class="py-1 mb-2 border-b">
        {{ mode === 'notification' ? 'Cette notification sera envoyée' : 'Ce message sera envoyé' }} aux patients
        sélectionnés :
      </p>
      <v-virtual-scroll
        :items="customers"
        item-key="id"
        :item-height="mode === 'notification' ? 64 : 96"
        max-height="60vh"
      >
        <template #default="{ item: customer }">
          <v-checkbox
            :model-value="selectedIds.has(customer.id)"
            density="comfortable"
            color="primary"
            class="border-b-2"
            :class="[{'h-16': mode==='notification', 'h-24': mode !== 'notification'}]"
            @update:model-value="checked => toggleCustomer(customer.id, checked)"
          >
            <template #label>
              <CommunicationCustomerRow class="pl-4" :customer="customer" :is-notification="mode==='notification'" />
            </template>
          </v-checkbox>
        </template>
      </v-virtual-scroll>
    </BaseCard>
    <div class="flex-1" />
    <div
      class="sticky shadow-deep shadow-black flex items-center pr-24 pl-4 py-4 bottom-0 w-full bg-white"
      :class="{'justify-end': mode === 'notification', 'justify-between': mode === 'sms' }"
    >
      <div v-if="mode === 'sms'">
        <p>Crédits restants: {{ credit?.balance || 0 }} {{ pluralize("crédit", credit?.balance || 0) }}</p>
        <BaseButton
          primary
          variant="text"
          density="compact"
          @click="creditStoreModalVisible = true"
        >
          Acheter des crédits
        </BaseButton>
        <CreditStoreModal v-if="creditStoreModalVisible" @close="creditStoreModalVisible = false" />
      </div>
      <div v-if="selectedCustomers.length > 0" class="flex flex-col items-end gap-1 text-end">
        <div class="flex items-center">
          <span v-if="mode==='sms'" class="px-4">
            Coût: {{ cost }} {{ $filters.pluralize('crédit', cost) }}
          </span>
          <BaseButton
            :disabled="!canSend"
            primary
            @click="onConfirm"
          >
            Envoyer {{ selectedCustomers.length }}
            {{ mode === 'notification' ? $filters.pluralize(mode, selectedCustomers.length) : mode }}
          </BaseButton>
        </div>
        <p v-if="!canSend" class="text-sm text-red-500">
          Vous n’avez pas assez de crédits pour envoyer ce message.
          <br>
          Vous pouvez acheter des crédits en cliquant <span
            class="font-bold cursor-pointer underline"
            @click="creditStoreModalVisible = true"
          >ici</span>.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.v-selection-control .v-label) {
    flex: 1;
    opacity: 1;
}
</style>
